<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import { fetchQuestionnaire, saveQuestionnaire } from '../../lib/convexApi';
  import type { QuestionDefinition, QuestionType } from '../../lib/types';
  import QuestionnaireRenderer from './QuestionnaireRenderer.svelte';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let communityId: string;

  const dispatch = createEventDispatcher<{ saved: { version: number } }>();

  const TYPE_LABELS: Array<{ value: QuestionType; label: string }> = [
    { value: 'text', label: 'Short text' },
    { value: 'textarea', label: 'Long text' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Phone' },
    { value: 'url', label: 'Link' },
    { value: 'number', label: 'Number' },
    { value: 'select', label: 'Single choice' },
    { value: 'multi-select', label: 'Multiple choice' },
    { value: 'slider', label: 'Scale' },
    { value: 'boolean', label: 'Yes / No' },
    { value: 'image', label: 'Photo upload' }
  ];

  const CHOICE_TYPES = new Set<QuestionType>(['select', 'multi-select']);

  let questions: QuestionDefinition[] = [];
  let loading = true;
  let saving = false;
  let error: string | null = null;
  let savedVersion: number | null = null;
  let dirty = false;
  let showPreview = false;
  let newType: QuestionType = 'text';

  function makeId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `q-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }

  onMount(async () => {
    try {
      const existing = await fetchQuestionnaire(communityId);
      questions = existing?.questions ? structuredClone(existing.questions) : [];
      savedVersion = existing?.version ?? null;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not load the questionnaire.';
    } finally {
      loading = false;
    }
  });

  function markDirty() {
    dirty = true;
    error = null;
  }

  function addQuestion() {
    const question: QuestionDefinition = {
      id: makeId(),
      type: newType,
      prompt: '',
      required: false
    };
    if (CHOICE_TYPES.has(newType)) {
      question.options = [{ label: '', value: makeId() }];
    }
    if (newType === 'slider') {
      question.min = 1;
      question.max = 10;
      question.step = 1;
    }
    questions = [...questions, question];
    markDirty();
  }

  function removeQuestion(index: number) {
    const prompt = questions[index]?.prompt || 'this question';
    if (!window.confirm(`Delete "${prompt}"?`)) return;
    questions = pruneConditions(questions.filter((_, i) => i !== index));
    markDirty();
  }

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    questions = pruneConditions(next);
    markDirty();
  }

  function addOption(question: QuestionDefinition) {
    question.options = [...(question.options ?? []), { label: '', value: makeId() }];
    questions = [...questions];
    markDirty();
  }

  function removeOption(question: QuestionDefinition, optionIndex: number) {
    question.options = (question.options ?? []).filter((_, i) => i !== optionIndex);
    questions = pruneConditions([...questions]);
    markDirty();
  }

  /** Conditions may only point backwards at a yes/no or single-choice question
   *  (and, for choices, at an option that still exists). Reordering or deleting
   *  can break that — drop any condition that no longer holds. */
  function pruneConditions(list: QuestionDefinition[]): QuestionDefinition[] {
    return list.map((question, index) => {
      const condition = question.showIf;
      if (!condition) return question;
      const target = list.slice(0, index).find(candidate => candidate.id === condition.questionId);
      const valid =
        target &&
        ((target.type === 'boolean' && typeof condition.equals === 'boolean') ||
          (target.type === 'select' &&
            (target.options ?? []).some(option => option.value === condition.equals)));
      return valid ? question : { ...question, showIf: undefined };
    });
  }

  /** Earlier questions the given index may depend on. */
  function conditionTargets(index: number, list: QuestionDefinition[]): QuestionDefinition[] {
    return list.slice(0, index).filter(q => q.type === 'boolean' || q.type === 'select');
  }

  function setConditionTarget(question: QuestionDefinition, event: Event) {
    const targetId = (event.target as HTMLSelectElement).value;
    if (!targetId) {
      question.showIf = undefined;
    } else {
      const target = questions.find(candidate => candidate.id === targetId);
      question.showIf = {
        questionId: targetId,
        equals: target?.type === 'boolean' ? true : target?.options?.[0]?.value ?? ''
      };
    }
    questions = [...questions];
    markDirty();
  }

  function setConditionValue(question: QuestionDefinition, event: Event) {
    if (!question.showIf) return;
    const raw = (event.target as HTMLSelectElement).value;
    const target = questions.find(candidate => candidate.id === question.showIf?.questionId);
    question.showIf = {
      ...question.showIf,
      equals: target?.type === 'boolean' ? raw === 'true' : raw
    };
    questions = [...questions];
    markDirty();
  }

  function conditionSummary(question: QuestionDefinition, index: number): string {
    return question.prompt.trim() || `Question ${index + 1}`;
  }

  function localValidate(): string | null {
    if (questions.length === 0) return 'Add at least one question.';
    for (const [index, question] of questions.entries()) {
      if (!question.prompt.trim()) return `Question ${index + 1} needs a prompt.`;
      if (CHOICE_TYPES.has(question.type)) {
        const options = question.options ?? [];
        if (options.length === 0 || options.some(option => !option.label.trim())) {
          return `Question ${index + 1}: every option needs a label.`;
        }
      }
      if (question.type === 'slider') {
        if (
          question.min === undefined ||
          question.max === undefined ||
          question.min >= question.max
        ) {
          return `Question ${index + 1}: scale needs min smaller than max.`;
        }
      }
    }
    return null;
  }

  /** Options keep their generated stable value; labels double as values for readability where fresh. */
  function normalizedForSave(): QuestionDefinition[] {
    return questions.map(question => ({
      ...question,
      prompt: question.prompt.trim(),
      helper: question.helper?.trim() || null,
      placeholder: question.placeholder?.trim() || null,
      options: CHOICE_TYPES.has(question.type)
        ? (question.options ?? []).map(option => ({
            label: option.label.trim(),
            value: option.value
          }))
        : undefined
    }));
  }

  async function handleSave() {
    const problem = localValidate();
    if (problem) {
      error = problem;
      return;
    }
    saving = true;
    error = null;
    try {
      const result = await saveQuestionnaire(communityId, normalizedForSave());
      savedVersion = result.version;
      dirty = false;
      dispatch('saved', { version: result.version });
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not save. Please try again.';
    } finally {
      saving = false;
    }
  }

  function typeLabel(type: QuestionType): string {
    return TYPE_LABELS.find(entry => entry.value === type)?.label ?? type;
  }
</script>

<div class="builder">
  {#if loading}
    <LoadingBlock label="Loading questionnaire…" />
  {:else}
    {#if error}
      <Notice variant="error">{error}</Notice>
    {/if}

    {#if showPreview}
      <div class="builder__preview surface surface--sunken">
        <div class="builder__preview-head">
          <h5>Preview</h5>
          <button type="button" class="ghost" on:click={() => (showPreview = false)}>Close preview</button>
        </div>
        <QuestionnaireRenderer
          questions={normalizedForSave()}
          submitLabel="Send application"
          on:submit={() => (showPreview = false)}
          on:cancel={() => (showPreview = false)}
        />
      </div>
    {:else}
      {#each questions as question, index (question.id)}
        <div class="builder__card">
          <div class="builder__card-head">
            <span class="builder__type">{typeLabel(question.type)}</span>
            <div class="builder__card-tools">
              <button type="button" class="ghost" disabled={index === 0} on:click={() => move(index, -1)} aria-label="Move up">↑</button>
              <button
                type="button"
                class="ghost"
                disabled={index === questions.length - 1}
                on:click={() => move(index, 1)}
                aria-label="Move down"
              >↓</button>
              <button type="button" class="ghost" on:click={() => removeQuestion(index)} aria-label="Delete question">✕</button>
            </div>
          </div>

          <input
            class="builder__input"
            type="text"
            placeholder="Question prompt"
            bind:value={question.prompt}
            on:input={markDirty}
          />
          <div class="builder__row">
            <input
              class="builder__input"
              type="text"
              placeholder="Helper text (optional)"
              bind:value={question.helper}
              on:input={markDirty}
            />
            {#if !CHOICE_TYPES.has(question.type) && question.type !== 'boolean' && question.type !== 'slider'}
              <input
                class="builder__input"
                type="text"
                placeholder="Placeholder (optional)"
                bind:value={question.placeholder}
                on:input={markDirty}
              />
            {/if}
          </div>

          {#if CHOICE_TYPES.has(question.type)}
            <div class="builder__options">
              {#each question.options ?? [] as option, optionIndex (option.value)}
                <div class="builder__option">
                  <input
                    class="builder__input"
                    type="text"
                    placeholder={`Option ${optionIndex + 1}`}
                    bind:value={option.label}
                    on:input={markDirty}
                  />
                  <button
                    type="button"
                    class="ghost"
                    on:click={() => removeOption(question, optionIndex)}
                    aria-label="Remove option"
                  >✕</button>
                </div>
              {/each}
              <button type="button" class="secondary builder__add-option" on:click={() => addOption(question)}>
                Add option
              </button>
            </div>
          {/if}

          {#if question.type === 'slider' || question.type === 'number'}
            <div class="builder__row builder__row--numbers">
              <label>
                Min
                <input class="builder__input" type="number" bind:value={question.min} on:input={markDirty} />
              </label>
              <label>
                Max
                <input class="builder__input" type="number" bind:value={question.max} on:input={markDirty} />
              </label>
              {#if question.type === 'slider'}
                <label>
                  Step
                  <input class="builder__input" type="number" min="1" bind:value={question.step} on:input={markDirty} />
                </label>
              {/if}
            </div>
          {/if}

          <div class="builder__row builder__row--footer">
            <label class="builder__required">
              <input type="checkbox" bind:checked={question.required} on:change={markDirty} />
              Required
            </label>

            {#if conditionTargets(index, questions).length > 0}
              <div class="builder__condition">
                <label>
                  Show only if
                  <select value={question.showIf?.questionId ?? ''} on:change={event => setConditionTarget(question, event)}>
                    <option value="">always shown</option>
                    {#each conditionTargets(index, questions) as target (target.id)}
                      <option value={target.id}>
                        “{conditionSummary(target, questions.indexOf(target))}”
                      </option>
                    {/each}
                  </select>
                </label>
                {#if question.showIf}
                  {@const target = questions.find(candidate => candidate.id === question.showIf?.questionId)}
                  <label>
                    is
                    <select
                      value={typeof question.showIf.equals === 'boolean' ? String(question.showIf.equals) : question.showIf.equals}
                      on:change={event => setConditionValue(question, event)}
                    >
                      {#if target?.type === 'boolean'}
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      {:else}
                        {#each target?.options ?? [] as option (option.value)}
                          <option value={option.value}>{option.label || option.value}</option>
                        {/each}
                      {/if}
                    </select>
                  </label>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      {/each}

      <div class="builder__add">
        <select bind:value={newType} aria-label="Question type">
          {#each TYPE_LABELS as entry (entry.value)}
            <option value={entry.value}>{entry.label}</option>
          {/each}
        </select>
        <button type="button" class="secondary" on:click={addQuestion}>Add question</button>
      </div>

      <div class="builder__footer">
        <span class="builder__status">
          {#if savedVersion !== null && !dirty}
            Saved · version {savedVersion}
          {:else if dirty}
            Unsaved changes
          {/if}
        </span>
        <div class="builder__footer-actions">
          <button type="button" class="ghost" on:click={() => (showPreview = true)} disabled={questions.length === 0}>
            Preview
          </button>
          <button type="button" class="accent" on:click={handleSave} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save questionnaire'}
          </button>
        </div>
      </div>
    {/if}
  {/if}
</div>

<style>
  .builder {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .builder__card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
  }

  .builder__card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
  }

  .builder__type {
    font-size: var(--font-size-2xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-text-amber);
    border: var(--border-ambient);
    border-radius: var(--radius-pill);
    padding: 1px var(--space-2);
  }

  .builder__card-tools {
    display: flex;
    gap: var(--space-1);
  }

  .builder__card-tools button {
    padding: var(--space-1) var(--space-2);
    font-size: var(--font-size-xs);
  }

  .builder__input {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
  }

  .builder__row {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .builder__row > * {
    flex: 1;
    min-width: 140px;
  }

  .builder__row--numbers label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    flex: 0 1 110px;
    min-width: 90px;
  }

  .builder__options {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .builder__option {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }

  .builder__option button {
    flex-shrink: 0;
    padding: var(--space-1) var(--space-2);
    font-size: var(--font-size-xs);
  }

  .builder__add-option {
    align-self: flex-start;
    font-size: var(--font-size-xs);
    padding: var(--space-2) var(--space-3);
  }

  .builder__required {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    flex: 0 0 auto;
    min-width: 0;
  }

  .builder__row--footer {
    align-items: center;
    justify-content: space-between;
  }

  .builder__row--footer > * {
    flex: 0 1 auto;
    min-width: 0;
  }

  .builder__condition {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .builder__condition label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .builder__condition select {
    max-width: 220px;
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-xs);
  }

  .builder__add {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }

  .builder__add select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .builder__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .builder__status {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .builder__footer-actions {
    display: flex;
    gap: var(--space-2);
  }

  .builder__preview {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-4);
    border-radius: var(--radius-md);
  }

  .builder__preview-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .builder__preview-head h5 {
    margin: 0;
    font-size: var(--font-size-sm);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }
</style>
